import { Link } from "react-router-dom";
import {
  ArrowRight,
  Truck,
  Package,
  ShieldCheck,
  Layers,
  Tag,
  Boxes,
  Calendar,
  Sparkles,
  MapPin,
} from "lucide-react";
import BigDemenageurCTA from "@/components/BigDemenageurCTA";

const Sections = ({ city }) => (
  <>
    {/* ======= SECTION 2 — Logistique de proximité ======= */}
    <section className="py-14 md:py-20 bg-[#EEF3F7] border-y border-[#cfdbe6]">
      <div className="container-cdm">
        <div className="max-w-3xl">
          <div className="overline" style={{ color: "#237cb5" }}>
            Logistique de proximité
          </div>
          <h2 className="mt-3 font-display font-extrabold text-[26px] sm:text-[30px] md:text-[36px] text-[#0f1d2b]">
            Vos cartons livrés à {city.name}
          </h2>
          <p className="mt-5 text-[15px] md:text-base text-[#3a4856] leading-relaxed">
            Oubliez la perte de temps liée aux trajets dans les magasins de
            bricolage pour essayer de faire entrer des cartons volumineux
            dans votre coffre. Nous pilotons l&apos;acheminement complet de
            vos fournitures directement chez vous.
          </p>
        </div>

        <div className="mt-8 grid grid-cols-1 md:grid-cols-2 gap-4">
          {[
            {
              title: "Maîtrise interne",
              body: "Notre propre équipe assure la manutention et le transport, sans aucun intermédiaire ni sous-traitant.",
              Icon: Truck,
            },
            {
              title: "Focus Île-de-France",
              body: "Une action ciblée sur la région francilienne qui nous permet de vous garantir une réactivité maximale et des produits livrés dans un état impeccable, prêts à l'emploi.",
              Icon: MapPin,
            },
          ].map(({ title, body, Icon }) => (
            <div
              key={title}
              className="bg-white rounded-2xl border border-[#cfdbe6] p-5 md:p-6"
            >
              <div className="flex items-center gap-2.5">
                <span className="flex items-center justify-center h-11 w-11 rounded-full bg-[#237cb5] text-white">
                  <Icon className="h-5 w-5" strokeWidth={2.4} />
                </span>
                <h3 className="font-display font-extrabold text-[16px] text-[#0f1d2b]">
                  {title}
                </h3>
              </div>
              <p className="mt-3 text-[14px] text-[#3a4856] leading-relaxed">
                {body}
              </p>
            </div>
          ))}
        </div>
      </div>
    </section>

    {/* ======= SECTION 3 — Gamme protections : 3 catégories avec chips de produits ======= */}
    <section className="py-14 md:py-20 bg-white">
      <div className="container-cdm">
        <div className="max-w-3xl">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-[#FFE9D6] border border-[#FF7A00]/30 text-[11px] uppercase tracking-[0.18em] font-bold text-[#FF7A00] mb-4">
            <ShieldCheck className="h-3.5 w-3.5" strokeWidth={2.6} />
            Une armure pour chaque objet
          </div>
          <h2 className="font-display font-extrabold text-[26px] sm:text-[30px] md:text-[36px] text-[#0f1d2b]">
            Découvrez notre gamme de protections disponibles
          </h2>
          <p className="mt-5 text-[15px] md:text-base text-[#3a4856] leading-relaxed">
            Pour éviter la casse, chaque type de bien nécessite un
            traitement adapté. Notre catalogue en ligne propose des
            solutions ciblées pour sécuriser l&apos;intégralité de votre
            logement.
          </p>
        </div>

        <div className="mt-10 grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-5">
          {[
            {
              title: "Les structures de transport",
              body: "Cartons petits formats pour vos livres et charges denses, cartons standards modulables, et penderies verticales pour transférer vos vêtements sur cintres sans les froisser.",
              Icon: Boxes,
              chips: [
                {
                  label: "Carton à livres",
                  to: "/produit/carton-a-livres",
                },
                {
                  label: "Carton standard renforcé",
                  to: "/produit/carton-standard-renforce",
                },
                {
                  label: "Carton penderie",
                  to: "/produit/carton-penderie-grand",
                },
              ],
            },
            {
              title: "Les barrières anti-chocs",
              body: "Rouleaux de film bulle à forte épaisseur pour envelopper la vaisselle, ainsi que des couvertures de déménagement pour préserver les surfaces vernies de vos meubles.",
              Icon: Layers,
              chips: [
                {
                  label: "Papier bulle 10 m",
                  to: "/produit/papier-bulle-10m",
                },
                {
                  label: "Couverture de déménagement",
                  to: "/produit/couverture-demenagement",
                },
                {
                  label: "Carton à vaisselle",
                  to: "/produit/carton-vaisselle",
                },
              ],
            },
            {
              title: "Les consommables de scellage",
              body: "Rubans adhésifs à fort pouvoir collant qui maintiennent la géométrie des boîtes sous la pression, complétés par des dévidoirs pour accélérer la fermeture.",
              Icon: Tag,
              chips: [
                {
                  label: "Rouleau d'adhésif",
                  to: "/produit/rouleau-adhesif",
                },
                {
                  label: "Dévidoir pistolet",
                  to: "/produit/devidoir-pistolet-adhesif",
                },
              ],
            },
          ].map(({ title, body, Icon, chips }) => (
            <div
              key={title}
              className="bg-[#EEF3F7] rounded-2xl border border-[#cfdbe6] p-5 md:p-6 flex flex-col"
            >
              <div className="flex items-center gap-2.5">
                <span className="flex items-center justify-center h-11 w-11 rounded-full bg-[#237cb5] text-white">
                  <Icon className="h-5 w-5" strokeWidth={2.4} />
                </span>
                <h3 className="font-display font-extrabold text-[16px] text-[#0f1d2b] leading-tight">
                  {title}
                </h3>
              </div>
              <p className="mt-3 text-[14px] text-[#3a4856] leading-relaxed">
                {body}
              </p>
              <div className="mt-5 flex flex-wrap gap-2">
                {chips.map(({ label, to }) => (
                  <Link
                    key={label}
                    to={to}
                    className="inline-flex items-center gap-1 px-3 py-1.5 rounded-full bg-white border border-[#cfdbe6] hover:border-[#237cb5] hover:text-[#237cb5] text-[12.5px] font-bold text-[#0f1d2b] transition-colors"
                    data-testid={`city-clamart-chip-${to.split("/").pop()}`}
                  >
                    {label}
                    <ArrowRight className="h-3 w-3" strokeWidth={2.6} />
                  </Link>
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>

    {/* ======= CTA Big Déménagement (au milieu) ======= */}
    <section className="py-12 bg-white">
      <div className="container-cdm">
        <BigDemenageurCTA testId={`city-${city.slug}-bigdem-cta-middle`} />
      </div>
    </section>

    {/* ======= SECTION 4 — Rétroplanning J-30 / J-15 / J-3 (countdown timeline) ======= */}
    <section className="py-14 md:py-20 bg-[#fdf9f3] border-y border-[#f0e6d4] relative overflow-hidden">
      <div
        aria-hidden="true"
        className="absolute -top-32 -left-24 h-72 w-72 rounded-full bg-[#FF7A00]/10 blur-3xl"
      />
      <div
        aria-hidden="true"
        className="absolute -bottom-32 -right-24 h-72 w-72 rounded-full bg-[#237cb5]/10 blur-3xl"
      />
      <div className="container-cdm relative">
        <div className="max-w-3xl">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-white border border-[#cfdbe6] text-[11px] uppercase tracking-[0.18em] font-bold text-[#237cb5] mb-4">
            <Calendar className="h-3.5 w-3.5" strokeWidth={2.6} />
            Rétroplanning expert
          </div>
          <h2 className="font-display font-extrabold text-[26px] sm:text-[30px] md:text-[36px] text-[#0f1d2b]">
            Quand faut-il commencer ses cartons&nbsp;?
          </h2>
          <p className="mt-5 text-[15px] md:text-base text-[#3a4856] leading-relaxed">
            Pour éviter le stress de la dernière minute et les cartons
            bouclés dans la précipitation, une bonne gestion du temps est
            fondamentale. Voici les repères de nos experts.
          </p>
        </div>

        <div className="mt-12 relative">
          {/* Horizontal countdown bar (desktop) */}
          <div
            aria-hidden="true"
            className="hidden md:block absolute top-[68px] left-12 right-12 h-0.5 bg-gradient-to-r from-[#237cb5] via-[#237cb5] to-[#FF7A00]"
          />

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-5">
            {[
              {
                badge: "J-30",
                sub: "1 mois avant",
                title: "Le tri et l'inutile",
                body: "C'est le moment idéal pour démarrer. Emballez tout ce qui n'est pas lié au quotidien immédiat : livres, bibelots, vêtements hors-saison, vaisselle de fête et contenu de la cave ou du grenier.",
                color: "#237cb5",
                bg: "#EEF3F7",
              },
              {
                badge: "J-15",
                sub: "2 semaines avant",
                title: "Le cœur du logement",
                body: "Attaquez-vous aux pièces secondaires, au linge de maison, à la majorité de la décoration et au matériel informatique ou multimédia que vous utilisez peu.",
                color: "#3b8fc7",
                bg: "#EEF3F7",
              },
              {
                badge: "J-3",
                sub: "3 jours avant",
                title: "Le sprint final",
                body: "Emballez la vaisselle quotidienne, les vêtements de la semaine et démontez les meubles qui le nécessitent. Vous ne devez garder à l'extérieur que le strict minimum pour les dernières nuits.",
                color: "#FF7A00",
                bg: "#FFE9D6",
              },
            ].map(({ badge, sub, title, body, color, bg }) => (
              <article
                key={badge}
                className="relative bg-white rounded-2xl border border-[#cfdbe6] p-6 md:p-7 flex flex-col items-center text-center"
              >
                {/* Large countdown circle */}
                <div className="relative">
                  <span
                    className="flex items-center justify-center h-[88px] w-[88px] rounded-full font-display font-extrabold text-2xl border-4 border-white shadow-[0_8px_20px_-12px_rgba(0,0,0,0.25)]"
                    style={{ backgroundColor: color, color: "#fff" }}
                  >
                    {badge}
                  </span>
                </div>
                <div
                  className="mt-4 inline-flex items-center gap-1 px-3 py-1 rounded-full text-[11px] uppercase tracking-[0.16em] font-bold"
                  style={{ backgroundColor: bg, color }}
                >
                  {sub}
                </div>
                <h3 className="mt-4 font-display font-extrabold text-[17px] text-[#0f1d2b] leading-tight">
                  {title}
                </h3>
                <p className="mt-3 text-[14px] text-[#3a4856] leading-relaxed">
                  {body}
                </p>
              </article>
            ))}
          </div>
        </div>

        <div className="mt-12 flex flex-wrap items-center gap-3">
          <Link
            to="/packs-cartons"
            className="inline-flex items-center gap-2 h-12 px-6 rounded-xl bg-[#237cb5] hover:bg-[#1f6da0] text-white font-bold text-[14px] transition-colors"
            data-testid="city-clamart-cta-packs"
          >
            <Package className="h-4 w-4" strokeWidth={2.6} />
            Anticiper avec un kit complet
            <ArrowRight className="h-4 w-4" strokeWidth={2.6} />
          </Link>
          <Link
            to="/cartons-demenagement"
            className="inline-flex items-center gap-2 h-12 px-6 rounded-xl bg-white border border-[#cfdbe6] hover:border-[#237cb5] hover:text-[#237cb5] text-[#0f1d2b] font-bold text-[14px] transition-colors"
            data-testid="city-clamart-cta-shop"
          >
            <Sparkles className="h-4 w-4" strokeWidth={2.6} />
            Voir tous les cartons
          </Link>
        </div>
      </div>
    </section>
    {/* ======= SECTION 5 — CTA retour département ======= */}
    <section className="py-10 md:py-14 bg-[#EEF3F7] border-y border-[#cfdbe6]">
      <div className="container-cdm">
        <Link
          to={`/cartons-demenagement/${city.deptSlug}`}
          className="inline-flex items-center gap-2 h-11 px-5 rounded-xl bg-white border border-[#cfdbe6] hover:border-[#237cb5] hover:text-[#237cb5] text-[#0f1d2b] font-bold text-[14px] transition-colors"
          data-testid="city-clamart-cta-back-dept"
        >
          <Package className="h-4 w-4" strokeWidth={2.6} />
          Voir la page {city.deptName} ({city.deptCode})
        </Link>
      </div>
    </section>
  </>
);

export default { Sections };
